import React, { useEffect, useRef, useState } from "react"
import { Box } from "@mui/material"
import { PLACEMENT_ROLES } from "../pages/constants"
import { floodFill } from "../utils/imageFunctions"

// Same grid -> pixel transform as ShowClimb's drawClimb
const GRID_SPACING_X = 2 * (106.0 / 4000) * 1020
const GRID_SPACING_Y = 2 * (82.9 / 3000) * 1024
const SCALE_X = GRID_SPACING_X / 8.0
const SCALE_Y = GRID_SPACING_Y / 8
const OFFSET_X = 25
const OFFSET_Y = -90
const BOARD_HEIGHT = 1024

/**
 * Read-only render of a climb on the kilter board image.
 * `climb` needs `{placements, placement_roles}`; null renders a blank board.
 */
export default function BoardCanvas({ climb, maxHeight = "45vh" }) {
  const canvasRef = useRef(null)
  const [holdsImage, setHoldsImage] = useState(null)
  const [resizeCount, setResizeCount] = useState(0)

  useEffect(() => {
    const image = new Image()
    image.src = "/images/kilter-holds.png"
    image.onload = () => setHoldsImage(image)
  }, [])

  useEffect(() => {
    const onResize = () => setResizeCount((count) => count + 1)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    if (!holdsImage || !canvasRef.current) {
      return
    }
    const inMemoryCanvas = document.createElement("canvas")
    inMemoryCanvas.width = holdsImage.width
    inMemoryCanvas.height = holdsImage.height
    const inMemoryContext = inMemoryCanvas.getContext("2d")
    inMemoryContext.drawImage(holdsImage, 0, 0)
    const imageData = inMemoryContext.getImageData(
      0,
      0,
      inMemoryCanvas.width,
      inMemoryCanvas.height
    )
    const data = imageData.data
    for (let i = 3; i < data.length; i += 4) {
      data[i] = 64
    }

    if (climb?.placements) {
      climb.placements.forEach((placement, index) => {
        const x = Math.floor(placement.x * SCALE_X + OFFSET_X)
        const y = Math.floor(BOARD_HEIGHT - placement.y * SCALE_Y - OFFSET_Y)
        const color =
          PLACEMENT_ROLES[climb.placement_roles[index]]?.color || [0, 0, 0]
        floodFill(imageData, x, y, color)
      })
    }
    inMemoryContext.putImageData(imageData, 0, 0)

    const canvas = canvasRef.current
    const parent = canvas.parentElement
    canvas.width = parent.offsetWidth
    canvas.height = parent.offsetHeight
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(inMemoryCanvas, 0, 0, canvas.width, canvas.height)
  }, [holdsImage, climb, resizeCount])

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        margin: "auto",
        aspectRatio: "1/1",
        maxHeight,
        maxWidth: maxHeight,
      }}
    >
      <img
        src="/images/kilter.jpg"
        alt="kilter board"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "transparent",
        }}
      />
    </Box>
  )
}
